import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {Matrix4, Quaternion, Vector3} from "@cm/math"
import {IsNonNull} from "@cm/utils"
import {TransformNode} from "#template/nodes/transform/transform-node"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {NumberLike, TransformLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import {transformLike} from "#template/nodes/transform/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"

export function transformMatrixFromTranslationRotationScale(translation: Vector3, rotationRd: Vector3, scale: Vector3): Matrix4 {
    return Matrix4.compose(translation, Quaternion.fromEuler(rotationRd.x, rotationRd.y, rotationRd.z), scale)
}

function evaluateListOfNumberLikes(scope: GraphBuilderScope, evaluator: NodeEvaluator, list: NumberLike[]): BuilderInlet<number[]> {
    return scope.branch(
        scope.pureLambda(
            scope.tuple(scope.sparseList(list.map((item) => evaluator.evaluate(item))), list.length),
            ([evaluatedList, listLength]) => {
                const validEvaluatedList = evaluatedList.filter(IsNonNull)
                return validEvaluatedList.length === listLength ? validEvaluatedList : null
            },
            "evaluatedListOfNumberLikes",
        ),
    )[0]
}

export type TransformMatrixParametersTranslationRotationScale = {
    type: "TranslationRotationScale"
    translation: [NumberLike, NumberLike, NumberLike]
    rotation: [NumberLike, NumberLike, NumberLike]
    scale: [NumberLike, NumberLike, NumberLike]
}

export type TransformMatrixParametersMatrixArray = {
    type: "MatrixArray"
    array: [
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
        NumberLike,
    ]
}

export type TransformMatrixParametersProduct = {
    type: "Product"
    termA: TransformLike | null
    termB: TransformLike | null
}

export type TransformMatrixParameters = {
    name: string
    transform: TransformMatrixParametersTranslationRotationScale | TransformMatrixParametersMatrixArray | TransformMatrixParametersProduct
}

const numberLikeTriple = z.tuple([numberLike, numberLike, numberLike])
const numberLike16 = z.tuple([
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
    numberLike,
])

const transformMatrixDefinition: TemplateNodeDefinition<TransformMatrixParameters> = {
    nodeClass: "TransformMatrix",
    validation: {
        parameters: z.object({
            name: z.string(),
            transform: z.discriminatedUnion("type", [
                z.object({type: z.literal("TranslationRotationScale"), translation: numberLikeTriple, rotation: numberLikeTriple, scale: numberLikeTriple}),
                z.object({type: z.literal("MatrixArray"), array: numberLike16}),
                z.object({type: z.literal("Product"), termA: z.nullable(transformLike), termB: z.nullable(transformLike)}),
            ]),
        }),
        returns: getTemplateNodeReturns(),
    },
}

export type TransformMatrixWithParametersTranslationRotationScale = TransformMatrix & {
    parameters: {transform: TransformMatrixParametersTranslationRotationScale}
}

export type TransformMatrixWithParametersMatrixArray = TransformMatrix & {
    parameters: {transform: TransformMatrixParametersMatrixArray}
}

export type TransformMatrixWithParametersProduct = TransformMatrix & {
    parameters: {transform: TransformMatrixParametersProduct}
}

export const isTransformMatrixWithParametersTranslationRotationScale = (x: unknown): x is TransformMatrixWithParametersTranslationRotationScale =>
    x instanceof TransformMatrix && x.parameters.transform.type === "TranslationRotationScale"

export const isTransformMatrixWithParametersMatrixArray = (x: unknown): x is TransformMatrixWithParametersMatrixArray =>
    x instanceof TransformMatrix && x.parameters.transform.type === "MatrixArray"

export const isTransformMatrixWithParametersProduct = (x: unknown): x is TransformMatrixWithParametersProduct =>
    x instanceof TransformMatrix && x.parameters.transform.type === "Product"

@registerTemplateGraphNode
export class TransformMatrix extends TransformNode<TransformMatrixParameters> {
    public override get definition() {
        return transformMatrixDefinition
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<number[] | null> {
        const {transform} = this.parameters
        switch (transform.type) {
            case "TranslationRotationScale": {
                const translation = evaluateListOfNumberLikes(scope.scope("translation"), evaluator, transform.translation)
                const rotation = evaluateListOfNumberLikes(scope.scope("rotation"), evaluator, transform.rotation)
                const scale = evaluateListOfNumberLikes(scope.scope("scale"), evaluator, transform.scale)

                return scope.pureLambda(
                    scope.tuple(translation, rotation, scale),
                    ([translation, rotation, scale]) =>
                        transformMatrixFromTranslationRotationScale(
                            Vector3.fromArray(translation),
                            Vector3.fromArray(rotation.map((v) => (v * Math.PI) / 180.0)),
                            Vector3.fromArray(scale),
                        ).toArray(),
                    "transformMatrix",
                )
            }
            case "MatrixArray": {
                const matrix = evaluateListOfNumberLikes(scope.scope("matrix"), evaluator, transform.array)
                return scope.pureLambda(matrix, (matrix) => (matrix.length === 16 ? matrix : null), "transformMatrixFromMatrix")
            }
            case "Product": {
                const matrixA = evaluator.evaluate(transform.termA)
                const matrixB = evaluator.evaluate(transform.termB)

                return scope.pureLambda(
                    scope.tuple(matrixA, matrixB),
                    ([a, b]) => (a && b ? Matrix4.fromArray(a).multiply(Matrix4.fromArray(b)).toArray() : null),
                    "transformMatrixProduct",
                )
            }
        }
    }
}
